import React, { useEffect, useState } from "react";
import { fetchReportData } from '../../services/api';
import "./reportsPage.css"
import {
  Container,
  Row,
  Col,
  Card,
  Spinner,
  Form,
  Alert,
  Table,
  Button,
  Collapse,
  Badge,
} from "react-bootstrap";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { toast} from 'react-toastify';
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import Filters from '../Dashboard/Filters';
import useDownloadChart from "../../hooks/useDownloadChart";

// Reusable color logic
const getBadgeColor = (value) => {
  if (value > 0) return "success";
  if (value < 0) return "danger";
  return "secondary";
};

const ReportsDashboard = () => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expandedArch, setExpandedArch] = useState({});
  const [filters, setFilters] = useState({
    archdeaconry: '',
    parish: '',
    congregation: '',
    start_date: '',
    end_date: '',
    data_type: ''
  });
  const { downloadChart } = useDownloadChart(expandedArch, setExpandedArch, reportData);
  const chartId = "reportTable";

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchReportData(filters);
      console.log(data);
      
      setReportData(data);
    } catch (err) {
        if (err.response?.status === 404) {
          toast.info('No Report data found for the selected filters');
        } else {
          toast.error('Failed to load report data');
          setError("Error fetching report data");
        }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getScopeLabel = () => {
    const { archdeaconry, parish, congregation } = filters;

    // Build labels in order
    const parts = [];

    if (archdeaconry) parts.push(archdeaconry);
    if (parish) parts.push(parish);
    if (congregation) parts.push(congregation);

    // If nothing is selected → default label
    if (parts.length === 0) {
      return "Across all congregations";
    }

    // Join them with arrows
    return parts.join(" → ");
  };

  const getDateLabel = () => {
    const { start_date, end_date, data_type } = filters;

    // If both dates selected → show range
    if (start_date && end_date) {
      return `${start_date} → ${end_date}`;
    }

    // If only start date
    if (start_date && !end_date) {
      return `From ${start_date}`;
    }

    // If only end date
    if (!start_date && end_date) {
      return `Up to ${end_date}`;
    }

    // If data_type selected (e.g., monthly, yearly, quarterly)
    if (data_type) {
      return `${data_type.charAt(0).toUpperCase() + data_type.slice(1)} report`;
    }

    // Default fallback
    return "All periods combined";
  };

  const handlePrint = () => {
    // Expand everything
    const allKeys = {};
    reportData.data.forEach(period => {
      period.breakdown.forEach(a => {
        const key = `${period.period}-${a.archdeaconry}`;
        allKeys[key] = true;
      });
    });
    setExpandedArch(allKeys);

    // Wait for DOM to update
    setTimeout(() => window.print(), 300);  
  };

  const toggleArch = (archName) => {
    setExpandedArch((prev) => ({
      ...prev,
      [archName]: !prev[archName],
    }));
  };

  const exportToExcel = () => {
    if (!reportData?.data) return;
    const ws = XLSX.utils.json_to_sheet(reportData.data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Report");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "Diocese_Report.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Diocesan Growth Report", 14, 10);
    autoTable(doc, {
      startY: 20,
      head: [["Period", "Archdeaconry", "Total Collections", "Attendance"]],
      body: reportData.data.flatMap((p) =>
        p.breakdown.map((a) => [
          p.period,
          a.archdeaconry,
          a.total_collections.toLocaleString(),
          a.total_attendance.toLocaleString(),
        ])
      ),
    });
    doc.save("Diocese_Report.pdf");
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p>Loading report data...</p>
      </div>
    );

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!reportData) return null;

  const { data, totals, insights } = reportData;
  const chartData = data.map((p) => ({
    period: p.period,
    collections: p.total_collections,
    attendance: p.total_attendance,
  }));

  const latestPeriod = data[data.length - 1];

  return (
    <Container fluid className="mt-4">
      <Row className="mb-4 align-items-center">
        <Col>
          <h3 className="fw-bold">📊 Diocesan Growth Dashboard</h3>
        </Col>
      </Row>
      <Row className="mb-3">
        <Col md="auto" className="d-flex align-items-center gap-2">
          <Filters 
            onFilterChange={setFilters} 
            initialFilters={filters} 
            page="reports"
          />
          <Button variant="primary" onClick={fetchReport}>
            GENERATE REPORTS
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        {/* <Col md="auto">
          <Button variant="outline-success" onClick={exportToExcel}>
            Export Excel
          </Button>
        </Col> */}
        <Col md="auto">
          <Button variant="outline-secondary" onClick={handlePrint}>
            Print Report
          </Button>
        </Col>
        <Col md="auto">
          <Button variant="outline-danger" onClick={() =>
              downloadChart(chartId, `${filters.data_type || 'Yearly'} Reports`, filters)
            }>
            Download Report
          </Button>
        </Col>
      </Row>

      {/* Summary Cards */}
      <Row className="g-3 mb-4">
        <Col md={3}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Total Collections</Card.Title>
              <h4 className="text-success">
                KES {totals.total_collections.toLocaleString()}
              </h4>
              <small className="text-muted">{getDateLabel()}</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm">
            <Card.Body>
              <Card.Title>Total Attendance</Card.Title>
              <h4 className="text-primary">
                {totals.total_attendance.toLocaleString()}
              </h4>
              <small className="text-muted">{getScopeLabel()}</small>
            </Card.Body>
          </Card>
        </Col>
        {latestPeriod?.comparison && (
          <>
            <Col md={3}>
              <Card className="shadow-sm">
                <Card.Body>
                  <Card.Title>Collection Change</Card.Title>
                  <h4
                    className={
                      latestPeriod.comparison["collections_change_%"] >= 0
                        ? "text-success"
                        : "text-danger"
                    }
                  >
                    {latestPeriod.comparison["collections_change_%"]}%
                  </h4>
                  <small>
                    From {latestPeriod.comparison.from_period} →{" "}
                    {latestPeriod.period}
                  </small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="shadow-sm">
                <Card.Body>
                  <Card.Title>Attendance Change</Card.Title>
                  <h4
                    className={
                      latestPeriod.comparison["attendance_change_%"] >= 0
                        ? "text-success"
                        : "text-danger"
                    }
                  >
                    {latestPeriod.comparison["attendance_change_%"]}%
                  </h4>
                  <small>
                    From {latestPeriod.comparison.from_period} →{" "}
                    {latestPeriod.period}
                  </small>
                </Card.Body>
              </Card>
            </Col>
          </>
        )}
      </Row>

      {/* Charts */}
      <Row className="g-4 mb-5">
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>Collections Over Time</Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="collections" fill="#198754" name="Collections (KES)" />
                </BarChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
        <Col md={6}>
          <Card className="shadow-sm">
            <Card.Header>Attendance Trends</Card.Header>
            <Card.Body>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="attendance" stroke="#0d6efd" />
                </LineChart>
              </ResponsiveContainer>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Insights */}
      {insights?.length > 0 && (
        <Card className="shadow-sm mb-5 border-success">
          <Card.Header>🤖 AI-Generated Insights</Card.Header>
          <Card.Body>
            <ul>
              {insights.map((tip, i) => (
                <li key={i}>{tip}</li>
              ))}
            </ul>
          </Card.Body>
        </Card>
      )}

      {/* Archdeaconry Rankings with Drill-down
      {latestPeriod && (
        <Card className="shadow-sm mb-5">
          <Card.Header>
            🏆 Archdeaconry Rankings ({latestPeriod.period})
          </Card.Header>
          <Card.Body>
            <Table bordered hover responsive>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Archdeaconry</th>
                  <th>Total Collections</th>
                  <th>Attendance</th>
                  <th>Collections Change</th>
                  <th>Attendance Change</th>
                </tr>
              </thead>
              <tbody>
                {latestPeriod.breakdown.map((a, index) => (
                  <React.Fragment key={index}>
                    <tr
                      onClick={() => toggleArch(a.archdeaconry)}
                      style={{ cursor: "pointer" }}
                    >
                      <td>{index + 1}</td>
                      <td>
                        {a.archdeaconry}{" "}
                        <Badge bg="info">{a.parishes.length} Parishes</Badge>
                      </td>
                      <td>{a.total_collections.toLocaleString()}</td>
                      <td>{a.total_attendance.toLocaleString()}</td>
                      <td>{a.comparison?.["collections_change_%"]} <Badge bg={getBadgeColor(a.comparison?.["collections_change_%"])}>
                          {a.comparison?.["collections_change_%"] > 0 ? "↑ Growth" : "↓ Decline"}
                        </Badge> from {a.comparison?.["from_period"]}</td>
                      <td>{a.comparison?.["attendance_change_%"]} <Badge bg={getBadgeColor(a.comparison?.["attendance_change_%"])}>
                          {a.comparison?.["attendance_change_%"] > 0 ? "↑ Growth" : "↓ Decline"}
                        </Badge> from {a.comparison?.["from_period"]}</td>
                    </tr>

                    <tr>
                      <td colSpan="5" className="p-0 border-0">
                        <Collapse in={expandedArch[a.archdeaconry]}>
                          <div className="p-3 bg-light">
                            <Table size="sm" bordered>
                              <thead>
                                <tr>
                                  <th>Parish</th>
                                  <th>Total Collections</th>
                                  <th>Attendance</th>
                                </tr>
                              </thead>
                              <tbody>
                                {a.parishes.map((p, i) => (
                                  <tr key={i}>
                                    <td>{p.parish}</td>
                                    <td>{p.total_collections.toLocaleString()}</td>
                                    <td>{p.total_attendance.toLocaleString()}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                        </Collapse>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )} */}
      <div id={chartId}>
        {reportData.data.map((period, pIndex) => (
          <Card className="shadow-sm mb-5" key={pIndex}>
            <Card.Header>
              🏆 Archdeaconry Rankings — <strong>{period.period}</strong>
            </Card.Header>

            <Card.Body>
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Archdeaconry</th>
                    <th>Total Collections</th>
                    <th>Attendance</th>
                    <th>Collections Change</th>
                    <th>Attendance Change</th>
                  </tr>
                </thead>

                <tbody>
                  {period.breakdown.map((a, index) => (
                    <React.Fragment key={index}>
                      {/* ROW — ARCHDEACONRY */}
                      <tr
                        onClick={() => toggleArch(`${period.period}-${a.archdeaconry}`)}
                        style={{ cursor: "pointer" }}
                      >
                        <td>{index + 1}</td>

                        <td>
                          {a.archdeaconry}{" "}
                          <Badge bg="info">{a.parishes.length} Parishes</Badge>
                        </td>

                        <td>{a.total_collections.toLocaleString()}</td>
                        <td>{a.total_attendance.toLocaleString()}</td>

                        <td>
                          {a.comparison?.["collections_change_%"] ?? "—"}%{" "}
                          {a.comparison && (
                            <Badge bg={getBadgeColor(a.comparison["collections_change_%"])}>
                              {a.comparison["collections_change_%"] > 0
                                ? "↑ Growth"
                                : "↓ Decline"}
                            </Badge>
                          )}
                        </td>

                        <td>
                          {a.comparison?.["attendance_change_%"] ?? "—"}%{" "}
                          {a.comparison && (
                            <Badge bg={getBadgeColor(a.comparison["attendance_change_%"])}>
                              {a.comparison["attendance_change_%"] > 0
                                ? "↑ Growth"
                                : "↓ Decline"}
                            </Badge>
                          )}
                        </td>
                      </tr>

                      {/* DRILLDOWN ROW */}
                      <tr>
                        <td colSpan="6" className="p-0 border-0">
                          <Collapse
                            in={
                              expandedArch[`${period.period}-${a.archdeaconry}`]
                            }
                          >
                            <div className="p-3 bg-light">
                              <Table size="sm" bordered>
                                <thead>
                                  <tr>
                                    <th>Parish</th>
                                    <th>Total Collections</th>
                                    <th>Attendance</th>
                                    <th>Collections Change</th>
                                    <th>Attendance Change</th>
                                  </tr>
                                </thead>

                                <tbody>
                                  {a.parishes.map((p, idx) => (
                                    <tr key={idx}>
                                      <td>{p.parish}</td>
                                      <td>{p.total_collections.toLocaleString()}</td>
                                      <td>{p.total_attendance.toLocaleString()}</td>
                                      <td>
                                        {p.comparison?.["collections_change_%"] ?? "—"}%{" "}
                                        {p.comparison && (
                                          <Badge bg={getBadgeColor(p.comparison["collections_change_%"])}>
                                            {p.comparison["collections_change_%"] > 0
                                              ? "↑ Growth"
                                              : "↓ Decline"}
                                          </Badge>
                                        )}
                                      </td>

                                      <td>
                                        {p.comparison?.["attendance_change_%"] ?? "—"}%{" "}
                                        {p.comparison && (
                                          <Badge bg={getBadgeColor(p.comparison["attendance_change_%"])}>
                                            {p.comparison["attendance_change_%"] > 0
                                              ? "↑ Growth"
                                              : "↓ Decline"}
                                          </Badge>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          </Collapse>
                        </td>
                      </tr>
                    </React.Fragment>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        ))}
      </div>

    </Container>
  );
};

export default ReportsDashboard;
